/**
 * Canonical member-status buckets used for tab filtering and sorting.
 * Raw values stored in the DB come from PadSplit's API and vary in spelling/casing
 * (e.g. "moving in", "moving out", "request", "inactive") vs. the older tokens
 * already in the codebase ("moving_in", "moved-out", "booking_request").
 * This helper normalises both into five fixed buckets so the UI never needs to
 * do exact-string matches against raw DB values.
 */
export type MemberStatusBucket =
  | "active"
  | "inactive"
  | "moving_in"
  | "moving_out"
  | "booking_request"
  | "terminated"
  | "unknown";

/**
 * Raw (lowercased, trimmed) status labels that map to the "active" bucket.
 * Exported so SQL-side ranking (e.g. the stay-preference ORDER BY in
 * `getMemberByPadsplitId`) can be built from the same source of truth as
 * `bucketMemberStatus` and never drift from it.
 */
export const ACTIVE_RAW_STATUSES = ["active", "current", "in residence", "resident"] as const;

/**
 * Raw (lowercased, trimmed) status labels that map to the "booking_request"
 * bucket. Legacy/manual rows use 'booking_request' while synced rows may use
 * 'request'; SQL rankings must treat all of these equivalently.
 */
export const BOOKING_REQUEST_RAW_STATUSES = ["booking_request", "request", "booking request", "pending"] as const;

/**
 * Map any raw or legacy member status string to a canonical bucket.
 * Matching is case-insensitive and trims surrounding whitespace.
 */
export function bucketMemberStatus(raw: string | null | undefined): MemberStatusBucket {
  if (!raw) return "unknown";
  const s = String(raw).toLowerCase().trim();
  if (!s) return "unknown";

  // ── moving in ──────────────────────────────────────────────────────────────
  if (s === "moving_in" || s === "moving in" || s === "move in" || s === "move_in") {
    return "moving_in";
  }

  // ── moving out / moved out ─────────────────────────────────────────────────
  if (
    s === "moving_out" ||
    s === "moving out" ||
    s === "moved_out" ||
    s === "moved-out" ||
    s === "moved out" ||
    s === "move out" ||
    s === "move_out"
  ) {
    return "moving_out";
  }

  // ── booking request ────────────────────────────────────────────────────────
  if ((BOOKING_REQUEST_RAW_STATUSES as readonly string[]).includes(s)) {
    return "booking_request";
  }

  // ── inactive / archived / retired booking requests ─────────────────────────
  // "rejected" / "canceled" are stamped by the stale-booking-request
  // retirement sweep (48h without moving in): rejected = host declined the
  // request, canceled = no decision recorded before the window closed. Both
  // clear the member out of the Booking Requests tab.
  if (
    s === "inactive" ||
    s === "archived" ||
    s === "rejected" ||
    s === "declined" ||
    s === "canceled" ||
    s === "cancelled" ||
    s === "expired"
  ) {
    return "inactive";
  }

  // ── approved booking request (moving-in pipeline) ──────────────────────────
  // Stamped by the retirement sweep when the host approved the request; the
  // harvest re-stamps the real residency status once PadSplit reflects it.
  if (s === "approved") {
    return "moving_in";
  }

  // ── active / in residence ──────────────────────────────────────────────────
  if ((ACTIVE_RAW_STATUSES as readonly string[]).includes(s)) {
    return "active";
  }

  // ── terminated ───────────────────────────────────────────────────────────
  if (s === "terminated" || s === "terminate" || s === "evicted" || s === "banned") {
    return "terminated";
  }

  return "unknown";
}

/** Only arrival-pipeline transitions represent a new move-in. */
export function isMoveInTransition(
  previousStatus: string | null | undefined,
  currentStatus: string | null | undefined,
): boolean {
  const previousBucket = bucketMemberStatus(previousStatus);
  return (
    bucketMemberStatus(currentStatus) === "active" &&
    (previousBucket === "booking_request" || previousBucket === "moving_in")
  );
}

/**
 * Bucket a member by its status, honouring the app-owned terminal stamps
 * `booking_request_retired_at` and `moved_out_retired_at`.
 *
 * `members.status` is harvest-owned: the external PadSplit harvest rewrites it
 * on every pass and has no rejection vocabulary, so a request the host
 * rejected INSIDE PadSplit keeps coming back as `request` no matter how many
 * times the hourly retire sweep flips it. Once a booking request has been
 * retired (by the sweep, by an in-app decision, or by the missing-member
 * archive job) we stamp `bookingRequestRetiredAt` — a column the harvest never
 * writes — and every "is this a live booking request?" read goes through here
 * instead of raw `bucketMemberStatus`, so the row can't bounce back into the
 * Booking Requests tab.
 *
 * `movedOutRetiredAt` is the moving-out-family sibling under the same
 * contract: PadSplit can keep reporting a resident as "moving out" past the
 * recorded move_out_date (extensions, delayed departures), so the demote
 * sweep stamps this column instead of fighting the harvest with status
 * writes, and a stamped moving-out row reads as inactive here. Occupancy
 * derivation (reconcile-room-statuses) classifies through this function, so
 * an UNstamped moving-out resident still counts as physically occupying
 * their room.
 *
 * Legacy move-out stamps were inferred from dates/silence, not verified
 * departure receipts. They suppress only moving-out status, never a later
 * active resident. Receipt-specific sticky precedence requires an audited,
 * stay-scoped source and observation time; the legacy marker cannot supply it.
 */
/** Explicit app-owned uncertainty is sticky for this stay, never inferred from dates. */
export function hasApplicationReviewHold(member: {
  occupantId?: string | null;
  applicantLifecycle?: { kind: string; occupantId: string | null; supersededAt?: string } | null;
}): boolean {
  const proof = member.applicantLifecycle;
  return proof?.kind === "needs_review" && !proof.supersededAt &&
    !!proof.occupantId?.trim() && proof.occupantId === member.occupantId;
}

/** An imported status or scheduled date cannot reverse reviewed stay evidence. */
export function hasUnresolvedApplicantEvidence(member: Parameters<typeof hasApplicationReviewHold>[0]): boolean {
  const proof = member.applicantLifecycle;
  return !!proof && ["needs_review", "never_moved_in"].includes(proof.kind) &&
    !proof.supersededAt && proof.occupantId === (member.occupantId ?? null) &&
    (proof.kind === "never_moved_in" || !!proof.occupantId?.trim());
}

export function effectiveMemberStatusBucket(
  member: {
    status?: string | null;
    occupantId?: string | null;
    applicantLifecycle?: { kind: string; occupantId: string | null; supersededAt?: string } | null;
    bookingRequestRetiredAt?: Date | string | null;
    movedOutRetiredAt?: Date | string | null;
  },
): MemberStatusBucket {
  if (hasApplicationReviewHold(member)) return "booking_request";
  if (hasUnresolvedApplicantEvidence(member)) return "inactive";
  const bucket = bucketMemberStatus(member.status);
  if (bucket === "booking_request" && member.bookingRequestRetiredAt != null) {
    return "inactive";
  }
  if (bucket === "moving_out" && member.movedOutRetiredAt != null) {
    return "inactive";
  }
  return bucket;
}

/** Broadcasts exclude applicants and inactive stays, not unretired departures.
 * Keep terminated/unknown handling compatible with the existing composer.
 */
export function isBroadcastMemberEligible(
  member: Parameters<typeof effectiveMemberStatusBucket>[0],
): boolean {
  const bucket = effectiveMemberStatusBucket(member);
  return bucket !== "booking_request" && bucket !== "inactive";
}

/**
 * The member lifecycle stages in flow order, as offered by the manual status
 * dropdown on the Members page. Values are the canonical tokens
 * `bucketMemberStatus` maps 1:1 onto its buckets, so a manually-set status
 * round-trips through every tab/badge/sweep exactly like a harvested one:
 * "booking_request" enters the 48h retirement window
 * (retire-stale-booking-requests) and "moving_out" the move-out demotion
 * (demote-moved-out-members).
 */
export const MEMBER_STATUS_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: "booking_request", label: "Requests" },
  { value: "moving_in", label: "Moving In" },
  { value: "active", label: "Active" },
  { value: "moving_out", label: "Moving Out" },
  { value: "terminated", label: "Terminated" },
  { value: "inactive", label: "Inactive" },
];

/**
 * Returns true for any bucket that represents a currently-housed member
 * (i.e. not inactive, not moved-out history, not a booking request).
 */
export function isActiveMemberBucket(bucket: MemberStatusBucket): boolean {
  return bucket === "active" || bucket === "moving_in" || bucket === "moving_out";
}

/**
 * Returns true for buckets that should be treated as fully-ended tenancy
 * (terminated or moved-out), distinct from a simple inactive/archived state.
 */
export function isEndedMemberBucket(bucket: MemberStatusBucket): boolean {
  return bucket === "terminated" || bucket === "moving_out";
}
